import { useState } from 'react';

import Button from 'honeydew/Button';
import Checkbox from 'honeydew/Checkbox';
import DrawerModal from 'shared/DrawerModal';

/**
 * Modal for adding a sold-out product to the recurring items list.
 * Unlike AddToRecurringModal, this modal's CTA triggers the add action.
 * Includes a checkbox to suppress future displays of this modal.
 *
 * @param {Object} props
 * @param {boolean} [props.isOpen=false] - Whether the modal is open
 * @param {string} [props.itemName='this item'] - The name of the product to add
 * @param {Function} props.onAddToRecurring - Callback when user confirms adding to recurring
 * @param {Function} props.onClose - Callback when modal is closed
 */
export function SoldOutRecurringModal({ isOpen = false, itemName = 'this item', onAddToRecurring, onClose }) {
    const [suppressFutureModals, setSuppressFutureModals] = useState(false);

    if (!isOpen) {
        return null;
    }

    const handleClose = () => {
        setSuppressFutureModals(false);
        onClose();
    };

    const handleAddToRecurring = () => {
        onAddToRecurring(suppressFutureModals);
        setSuppressFutureModals(false);
    };

    return (
        <DrawerModal
            onClose={handleClose}
            drawerProps={{
                sx: { zIndex: 6000 },
            }}
            modalProps={{
                centered: true,
                styles: { contentRoot: 'py-40 px-60' },
                includeHorizontalPadding: false,
            }}
        >
            <div className="flex flex-col">
                <h2 className="text-heading-xs mb-15 md:mb-10">
                    Want {itemName} added to your cart when it&apos;s back in stock?
                </h2>
                <p className="mb-15 text-body-mobile-sm md:mb-20 md:text-body-mobile-md">
                    By adding it to your recurring list, we&apos;ll automatically add it to your cart when it&apos;s
                    available, so you&apos;ll never forget your favorite products. Recurring items are charged in
                    addition to your pre-filled cart (and are subject to availability!).
                </p>
                <Checkbox
                    id="suppress-sold-out-recurring-modal"
                    label="Don't show me this again"
                    labelStyle="small"
                    checked={suppressFutureModals}
                    onCheckedChange={setSuppressFutureModals}
                    styles={{ label: 'text-body-sm' }}
                    wrapperClassName="mb-20"
                />
                <Button variant="primary" onClick={handleAddToRecurring}>
                    Add to Recurring
                </Button>
            </div>
        </DrawerModal>
    );
}
